import { ReactComponent as DisplayClockIcon } from "assets/images/icons/icn_display_clock.svg";

import React, { useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import useDataSourceManager from "hooks/useDataSourceManager";

import LineChart from "components/LineChart";
import InfoCardField from "components/cards/InfoCardField";
import GlobalAlert from "components/Notification";
import { getLineChartData } from "utils/getEChartData";

// API
import Api from "../util/api";

// Const
import { defaultTrafficData, uuids } from '../util/const';

// Telemetry timestamps are the start of a fifteen-minute aggregate bucket.
const FIFTEEN_MINUTE_BUCKET_MS = 15 * 60 * 1000;

const LoadingContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: 2,
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  margin: 2,
}));

const StatisticContent = ({
  auth,
  selectedClient,
  selectedSiteId,
}) => {
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [trafficData, setTrafficData] = useState([]);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const api = Api.init(auth, selectedSiteId);

  const fetchStatisticsData = () => {
    if (!selectedClient) return;

    const { apiKey, params } = api.getStatisticsRequestParams(selectedClient.clientId);

    sendRequest(
      apiKey,
      uuids.statistics,
      params,
    );
  }

  useEffect(() => {
    fetchStatisticsData();
  }, [selectedClient]);

  useEffect(() => {
    const response = getData(uuids.statistics);
    const responseData = response?.data ?? null;

    if (responseData) {
      setStatisticsLoading(true);

      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: responseData.error?.message || 'Failed to get client statistics.',
        });
      } else {
        const { downLinkStats, upLinkStats } = responseData;

        const state = {
          downLinkStats: downLinkStats ?? defaultTrafficData.downLinkStats,
          upLinkStats: upLinkStats ?? defaultTrafficData.upLinkStats,
        };

        const chartData = getLineChartData({ downLinkStats: "Downlink", upLinkStats: "Uplink" }, state, "KB");

        setTrafficData(chartData);
        setStatisticsLoading(false);
      }
    }
  }, [dataMap]);

  if (!selectedClient) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          p: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <Box width="100%" padding="16px">
          <InfoCardField
            mode="stack"
            title="Traffic"
            metaInfo={{ icon: <DisplayClockIcon />, info: "Last 24 Hours" }}
            infoData={(
              statisticsLoading ? (
                <LoadingContainer>
                  <CircularProgress />
                </LoadingContainer>
              ) : (
                <LineChart
                  legendStyle={{ top: 18, right: 10 }}
                  lineChartData={trafficData}
                  unitData={{ yAxisName: "Rate (Kbps)", tooltipUnit: "Kbps", timeRangeUnit: FIFTEEN_MINUTE_BUCKET_MS, timeRangePosition: "start" }}
                  height="400px"
                />
              )
            )}
          />
        </Box>
      </Box >

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default StatisticContent;
